/**
 * Audio Mode Hook
 * Manages the autoplay preference: on (auto_play) or off (mute).
 * Shared between ConversationHubScreen and ProfileScreen via localPreferences.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getLocalPreferences,
  isLocalPreferencesLoaded,
  loadLocalPreferences,
  updateLocalPreferences,
} from '../services/localPreferences';
import type { AudioMode } from '../services/localPreferences';

// Re-export so existing consumers keep working
export type { AudioMode };

const DEFAULT_MODE: AudioMode = 'auto_play';

// ── Module-level cache for non-hook access ──────────────────────────
// Loaded once at startup, kept in sync by setMode().
let _cachedMode: AudioMode = DEFAULT_MODE;
let _cacheLoaded = false;

/** Normalize legacy 'play_on_demand' → 'auto_play'. */
function normalize(mode: string): AudioMode {
  if (mode === 'auto_play') return 'auto_play';
  if (mode === 'play_on_demand') return 'auto_play'; // migrate old value
  return 'mute';
}

/** Load cache from localPreferences (call once at app startup). */
export async function loadAudioModeCache(): Promise<void> {
  try {
    const prefs = isLocalPreferencesLoaded()
      ? getLocalPreferences()
      : await loadLocalPreferences();
    _cachedMode = normalize(prefs.audioMode);
  } catch { /* ignore */ }
  _cacheLoaded = true;
}

/**
 * Synchronous getter for use in stores/services outside React components.
 * Returns the cached value (loaded at startup). Falls back to DEFAULT_MODE if not yet loaded.
 */
export function getAudioMode(): AudioMode {
  return _cachedMode;
}

/** Convenience: true when autoplay is enabled. */
export function isAutoplayEnabled(): boolean {
  return _cachedMode === 'auto_play';
}

export function useAudioMode() {
  const [mode, setModeState] = useState<AudioMode>(DEFAULT_MODE);

  // Intentional: sync state from module-level cache on mount
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (_cacheLoaded) {
      setModeState(_cachedMode);
    } else {
      loadAudioModeCache().then(() => setModeState(_cachedMode));
    }
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const setMode = useCallback(async (newMode: AudioMode) => {
    setModeState(newMode);
    _cachedMode = newMode; // Keep module cache in sync
    await updateLocalPreferences({ audioMode: newMode });
  }, []);

  const toggle = useCallback(async () => {
    const nextMode: AudioMode = mode === 'mute' ? 'auto_play' : 'mute';
    await setMode(nextMode);
  }, [mode, setMode]);

  /** Whether autoplay is on. */
  const autoplay = mode === 'auto_play';

  return { mode, autoplay, setMode, toggle };
}
